import { useEffect, useState, ReactNode, useRef } from 'react';
import { initKeycloak, keycloak } from '../../lib/keycloak';
import { useAuthStore } from '../../stores/auth.store';
import { api } from '../../lib/api';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [initialized, setInitialized] = useState(false);
  const initializingRef = useRef(false);

  useEffect(() => {
    // Prevent multiple initializations
    if (initializingRef.current || initialized) {
      return;
    }
    initializingRef.current = true;

    const init = async () => {
      useAuthStore.getState().setLoading(true);
      try {
        await initKeycloak();
        useAuthStore.getState().updateFromKeycloak();

        // Track login event (fire and forget, once per user per session)
        if (keycloak.authenticated && keycloak.subject) {
          const loginKey = `bpa_login_tracked_${keycloak.subject}`;
          if (!sessionStorage.getItem(loginKey)) {
            api.post('/users/me/login').catch(() => {});
            sessionStorage.setItem(loginKey, '1');
          }
        }

        // Listen for token changes
        keycloak.onAuthSuccess = () => {
          useAuthStore.getState().updateFromKeycloak();
        };

        keycloak.onAuthRefreshSuccess = () => {
          useAuthStore.getState().updateFromKeycloak();
        };

        keycloak.onAuthLogout = () => {
          useAuthStore.getState().updateFromKeycloak();
        };

        keycloak.onTokenExpired = () => {
          keycloak.updateToken(30).catch(() => {
            keycloak.login();
          });
        };
      } catch (error) {
        console.error('Failed to initialize Keycloak:', error);
      } finally {
        useAuthStore.getState().setLoading(false);
        setInitialized(true);
      }
    };

    init();
  }, [initialized]);

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Initializing...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
